<template>
  <div class="product-grid">
    <div v-if="initialProductsCount || !$slots.noResults || pending" class="filter-page">
      <FilterHeader
        :primaryHeading="primaryHeading"
        :showFilters="showFilters"
        :sort="sort"
        @update:sort="updateSort($event)"
        :filterCount="filterCount"
        @openFilterDrawer="openFilterDrawer"
      ></FilterHeader>
      <div class="flex-grid no-gutter container mx-width">
        <FilterList
          v-if="showFilterSection"
          class="desktop-filters flex-col-3-lg flex-col-2-xl flex-col-1-xxl"
          :class="{ 'moved-up': !primaryHeading && !$slots.primaryHeading }"
          @clearFilter="clearFilter"
          :filterCount="filterCount"
          :filter="filter"
          @update:filter="updateFilter($event)"
          :taxonomyCategories="taxonomyCategories"
          :taxonomyAttributes="taxonomyAttributes"
          :saleCount="onSaleCount"
          :clearanceCount="onClearanceCount"
          :stockCount="inStockCount"
          :showStockFilter="!initialFilter.inStock && !!inStockCountInitial"
          :showSaleFilter="!initialFilter.onSale && !!onSaleCountInitial"
          :showClearanceFilter="!initialFilter.onClearance && !!onClearanceCountInitial"
        ></FilterList>
        <div
          class="product-list flex-col-12-xs flex-col-12-sm flex-col-12-md flex-col-10-xl flex-col-11-xxl"
          :class="{
            'flex-col-9-lg': showFilterSection,
            'flex-col-12-lg': !showFilterSection,
            'flex-col-9-xl': showFilterSection,
            'flex-col-12-xl': !showFilterSection,
          }"
        >
          <div class="product-grid-layout" id="grid-layout-tiles">
            <template v-for="(n, productIndex) in products.length" :key="productIndex">
              <!-- Products -->
              <template v-if="(mounted || productIndex <= 11) && (productsBatch.isLastBatch || productIndex < productsBatch.numOfItems)">
                <div class="product-item" :class="[!products[productIndex] && 'product-item-skeleton']" :id="productIndex">
                  <product :product="products[productIndex] || {}" :badge="true" :lazy="productIndex > 11"></product>
                </div>
              </template>
  
              <!-- Tiles -->
              <template v-if="mounted && productIndex === 0">
                <ProductGridTile
                  :key="tileIndex"
                  v-for="(tile, tileIndex) in tiles"
                  :content="tile.tileContent"
                  :size="tile.tileSize"
                  :productsCount="products.length"
                  :productGridColCount="productGridColCount"
                ></ProductGridTile>
              </template>
            </template>
          </div>
          <div v-show="isNoProducts" class="empty-results flex-col-12-xs flex-col-12-sm flex-col-12-md flex-col-12-lg">
            No products match the filters.
          </div>
          <div class="see-more-button" v-if="products.length < productsCount">
            <div class="see-more-divider">
              <Divider margin-top="var(--space-6)" margin-bottom="var(--space-6)" />
            </div>
            <div class="see-more-count">Showing {{ productsBatch.numOfItems }} of {{ productsCount }}</div>
            <button class="button grey outlined" @click="loadMore" :disabled="pending">
              <div v-if="pending" class="loader tiny"></div>
              <template v-else>See More</template>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <slot name="noResults"></slot>
    </div>

    <FilterDrawer
      v-if="isNavigationDrawerLoaded"
      v-model:open="isNavigationDrawerOpen"
      @clearFilter="clearFilter"
      :filterCount="filterCount"
      :filter="filter"
      @update:filter="updateFilter($event)"
      :taxonomyCategories="taxonomyCategories"
      :taxonomyAttributes="taxonomyAttributes"
      :sort="sort"
      @update:sort="updateSort($event)"
      :productsCount="productsCount"
      :saleCount="onSaleCount"
      :clearanceCount="onClearanceCount"
      :stockCount="inStockCount"
      :showStockFilter="!initialFilter.inStock && !!inStockCountInitial"
      :showSaleFilter="!initialFilter.onSale && !!onSaleCountInitial"
      :showClearanceFilter="!initialFilter.onClearance && !!onClearanceCountInitial"
    ></FilterDrawer>
  </div>
</template>
<script>
import { Divider, Lazy, Select, UrlUtils } from '@/pixel/index';
import smoothReflow from '@/plugins/smoothReflow';
import BrowseProduct from './BrowseProduct.vue';
import FilterHeader from '@/components/browseNa/FilterHeader.vue';
import FilterDrawer from '@/components/browseNa/FilterDrawer.vue';
import FilterList from '@/components/browseNa/FilterList.vue';
import useProductSearch from '@/compositions/useProductSearch';
import { Sort, SORT_ID_TO_QUERY_NAME, SORT_QUERY_NAME_TO_ID } from '@/components/commons/SortOptions.js';
import {
  computed, //
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';
import ProductGridTile from '@/components/browseNa/ProductGridTile.vue';
import useResize from '@/compositions/useResize.js';
import useGridTile from '@/compositions/useGridTile';

export default {
  name: 'ProductGrid',
  components: {
    'select-input': Select,
    product: BrowseProduct,
    Lazy,
    FilterDrawer,
    FilterList,
    Divider,
    FilterHeader,
    ProductGridTile,
  },

  mixins: [smoothReflow],

  props: {
    id: String,
    primaryHeading: String,
    showFilters: {
      type: Boolean,
      default: true,
    },
    initialFilter: {
      type: Object,
    },
    pageSize: {
      type: Number,
      default: 24,
    },
    defaultSort: {
      type: String,
      default: Sort.Popularity?.id,
    },
    tiles: {
      type: Array,
      default: [],
    },
  },

  async setup(props, context) {
    const {
      pending,
      initialFilter,
      filter,
      sort,
      updateInitialFilter,
      updateFilter,
      updatePageSize,
      updateDefaultSort,
      updateSort,
      clearFilter,
      products,
      maxPage,
      loadedPageCount,
      taxonomyCategories,
      taxonomyAttributes,
      isNoProducts,
      productsCount,
      inStockCountInitial,
      onSaleCountInitial,
      onClearanceCountInitial,
      inStockCount,
      onSaleCount,
      onClearanceCount,
      queryAfterRemoval,
      queryID,
      filterCount,
      load,
      loadMore,
    } = useProductSearch();

    const router = useRouter();
    const { width } = useResize();
    const mounted = ref(false);

    const isNavigationDrawerOpen = ref(false);
    const isNavigationDrawerLoaded = ref(false);

    const initialProductsCount = computed(() => products.value.length);
    const showFilterSection = computed(
      () =>
        (props.showFilters && taxonomyAttributes.value.length) ||
        taxonomyCategories.value.length ||
        (props.showSaleFilters && (onSaleCountInitial.value || onClearanceCountInitial.value)),
    );

    const productsBatch = computed(() => {
       if(products.value.length === productsCount.value) { // productsCount is total number of products
         return {
            length: products.value.length,
            isLastBatch: true
          }
       }

      const totalGridCells = {
        ratio320px: 0,
        ratio768px: 0,
        ratio1200px: 0,
        ratio1400px: 0,
        ratio1800px: 0,
      };
    
      // Get total number of grid cells the promo tiles will occupy
      props.tiles.forEach((tile) => {
        const { tileGridCellCount } = useGridTile({
          tileSizes: tile.tileSize,
          productsCount: products.value.length,
          productGridColCount,
        });

        Object.keys(totalGridCells).forEach((element) => {
          if(tileGridCellCount.value[element])
            totalGridCells[element] += tileGridCellCount.value[element];
        });
      })   
           
      return {
        numOfItems: products.value.length - getDanglingItemsCount(totalGridCells),
        isLastBatch: false
      }
    })

    /**
     * Determine the number of dangling items in a product grid based on "grid column count"
     * and total number of tiles (num of promo tiles + num of product tiles on that page) per screen ratio
     */
    function getDanglingItemsCount(totalGridCells) {
      if (width.value >= 1600) // 4 cols
        return totalGridCells.ratio1800px % productGridColCount.value;
      else if (width.value >= 1367) // 3 cols
        return totalGridCells.ratio1400px % productGridColCount.value;
      else if (width.value >= 768) // 2 cols
        return totalGridCells.ratio1200px % productGridColCount.value;
      else if (width.value < 768)// 1 col - no such thing as dangling items 
        return 0
      else
        return 0;
    }

    function updateFilterFromURL() {
      const query = router.currentRoute.value.query ?? {};

      if (Object.keys(query).length == 0) return;

      const filtersFromQuery = {};

      const getParamValue = (key) => {
        try {
          return JSON.parse(query[key] ?? '{}')[props.id];
        } catch (e) {
          // SyntaxError when query.key isn't JSON
          return undefined;
        }
      };

      filtersFromQuery.taxonomyCategories = getParamValue('tc')?.split(',') ?? [];
      filtersFromQuery.taxonomyAttributes = getParamValue('av')?.split(',') ?? [];
      filtersFromQuery.inStock = getParamValue('stock') ?? filter.inStock;
      filtersFromQuery.onSale = getParamValue('sale') ?? filter.onSale;
      filtersFromQuery.onClearance = getParamValue('clearance') ?? filter.onClearance;

      const sortValue = getParamValue('sort');

      if (sortValue) updateSort(SORT_QUERY_NAME_TO_ID[sortValue]);

      updateFilter(filtersFromQuery);
    }

    function updateUrlQueryParams(newFilter, newSort) {
      // sort will be removed form url when undefined.
      const sort = newSort !== props.defaultSort ? SORT_ID_TO_QUERY_NAME[newSort] : undefined;

      UrlUtils.updateQueryParameterByNestedKey(props.id, {
        tc: newFilter.taxonomyCategories && newFilter.taxonomyCategories.length ? newFilter.taxonomyCategories.join(',') : '',
        av: newFilter.taxonomyAttributes && newFilter.taxonomyAttributes.length ? newFilter.taxonomyAttributes.join(',') : '',
        sort,
        sale: newFilter.onSale,
        stock: newFilter.inStock,
        clearance: newFilter.onClearance,
      });
    }

    function openFilterDrawer() {
      isNavigationDrawerLoaded.value = true;
      isNavigationDrawerOpen.value = true;
    }

    onMounted(() => {
      window.setTimeout(() => {
        isNavigationDrawerLoaded.value = true;
      }, 4000);

      mounted.value = true;
    });

    updatePageSize(props.pageSize);
    updateDefaultSort(props.defaultSort);
    updateInitialFilter(props.initialFilter);

    updateFilterFromURL();

    const productGridColCount = computed(() => {
      if (width.value >= 1600) {
        return 4;
      } else if (width.value >= 1367) {
        return 3;
      } else if (width.value >= 768) {
        return 2;
      } else if (width.value < 768) {
        return 1;
      } else {
        return 0;
      }
    });

    watch(
      [filter, sort],
      ([newFilter, newSort]) => {
        updateUrlQueryParams(newFilter, newSort);
        load(0);
      },
      { deep: true },
    );

    await load(0);

    return {
      pending,
      initialFilter,
      filter,
      sort,
      updateInitialFilter,
      updateFilter,
      updatePageSize,
      updateDefaultSort,
      updateSort,
      clearFilter,
      products,
      maxPage,
      loadedPageCount,
      taxonomyCategories,
      taxonomyAttributes,
      isNoProducts,
      productsCount,
      inStockCountInitial,
      onSaleCountInitial,
      onClearanceCountInitial,
      inStockCount,
      onSaleCount,
      onClearanceCount,
      queryAfterRemoval,
      queryID,
      filterCount,
      load,
      loadMore,
      initialProductsCount,
      showFilterSection,
      openFilterDrawer,
      isNavigationDrawerOpen,
      isNavigationDrawerLoaded,
      mounted,
      productGridColCount,
      productsBatch
    };
  },
};
</script>
<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.product-grid-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0px 0px;

  @media (min-width: $mobile-breakpoint) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1367px) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media (min-width: $extra-large-breakpoint) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
}

.filter-page {
  margin-top: 20px;

  .loading {
    transition: opacity 1000ms ease-out;
    opacity: 1;

    &.hide-loader {
      opacity: 0;
    }
  }

  .filters > * {
    margin-top: 15px;
  }

  .filters > *:first-child {
    margin-top: 5px;
  }

  .product-list {
    margin-top: 20px;

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      &.mobile-disable-margin-top {
        margin-top: 0px;
      }
    }
  }

  .empty-results {
    text-align: center;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .loading {
    text-align: center;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .desktop-filters {
    margin-bottom: 30px;

    &.moved-up {
      margin-top: -39px;
    }
  }

  @media (max-width: calc($tablet-breakpoint - 1px)) {
    .desktop-filters {
      display: none;
    }
  }
}

@media (min-width: $medium-desktop-breakpoint) {
  .product-grid {
    .desktop-filters.flex-col-1-xxl {
      width: 195px;
      flex: 0 0 195px;
      max-width: none;
    }

    .product-list.flex-col-11-xxl {
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: calc(100% - 195px);
    }
  }
}

.see-more-button {
  max-width: 200px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: var(--space-6);
}

.see-more-count {
  margin-bottom: var(--space-2);
  color: var(--grey-medium);
}

.see-more-divider {
  max-width: 40px;
  margin-left: auto;
  margin-right: auto;
}
</style>
